import React, { useRef, useState } from 'react';

import { quizMessages } from '../../../../../translates/quizMessages';
import { widgetMessages } from '../../../../../translates/widgetMessages';
import { useContentRequirementsContext } from '../../../../features/RestrictNavigation/ContentRequirementsProvider';
import { useDndDropPlaceholder } from '../../../../hooks/useDndDropPlaceholder';
import { useIntlReexport } from '../../../../hooks/useIntlReexport';
import { Guid } from '../../../../store/model/guid';
import { DndPlaceholder } from '../../../components/common/DragAndDrop/DndPlaceholder';
import { DroppableWrapper } from '../../../components/common/DragAndDrop/DroppableWrapper';
import { SortationOption, SortationOptionProps } from '../../../components/common/SortationWidget/SortationOption';
import { SortationOptions } from '../../../components/common/SortationWidget/SortationOptions';
import { AnswerMessage } from '../../../components/common/Widget/AnswerMessage';
import { Footer } from '../../../components/common/Widget/Footer';
import { Title } from '../../../components/common/Widget/Title';
import { TitleCaption } from '../../../components/common/Widget/TitleCaption';
import { StudentWidgetWrapper } from '../../../components/Widgets/StudentWidgetWrapper';
import { ExistingStudentWidgetProps } from '../Widget';
import { shuffleForSure } from './SortationWidgetApi';
import { SortationOptionType, SortationWidget as SortationWidgetModel } from '@ael/core-api';
import { StudentButton } from '@ael/shared-components-library';
import { DragDropContext, DragDropContextProps, Draggable, Droppable } from '@hello-pangea/dnd';
import { CSSTransition } from 'react-transition-group';

export const SortationWidget: React.FC<ExistingStudentWidgetProps> = ({ widget }) => {
  const sortationWidget = widget as SortationWidgetModel;
  const { formatMessage } = useIntlReexport();
  const footerRef = useRef(null);

  const [studentOrder, setStudentOrder] = useState(shuffleForSure(sortationWidget.value.data.optionsCorrectOrder));
  const [draggedOption, setDraggedOption] = useState<Guid | undefined>();
  const dropPlaceholder = useDndDropPlaceholder();
  const [submitted, setSubmit] = useState(false);
  const [showCorrectAnswer, setShowCorrectAnswerResult] = useState(false);
  const { submitWidget } = useContentRequirementsContext();

  const { repeatUntilCorrect, revealAnswers } = sortationWidget.value.properties;

  const dragEndHandle: DragDropContextProps['onDragEnd'] = (result) => {
    if (!result.destination) {
      return;
    }
    const items = studentOrder;
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setStudentOrder(items);
    setDraggedOption(undefined);
  };

  const onDragStartHandle: DragDropContextProps['onDragStart'] = (initial) => {
    dropPlaceholder.onDragStart(initial);
    setDraggedOption(initial.draggableId);
  };

  const onDragUpdateHandle: DragDropContextProps['onDragUpdate'] = (initial) => {
    dropPlaceholder.onDragUpdate(initial);
  };

  const submit = () => {
    setSubmit(true);
    const fulfillWithIncorrect = !repeatUntilCorrect;
    submitWidget(widget, isOrderCorrect, fulfillWithIncorrect);
  };

  const tryAgain = () => {
    setSubmit(false);
    setShowCorrectAnswerResult(false);
    setStudentOrder(shuffleForSure(sortationWidget.value.data.optionsCorrectOrder));
  };

  const pickVariant = (option: SortationOptionType, index: number): SortationOptionProps['variant'] => {
    if (showCorrectAnswer) {
      return 'solution';
    }

    return option.text === sortationWidget.value.data.optionsCorrectOrder[index].text ? 'correct' : 'incorrect';
  };

  const isOrderCorrect =
    JSON.stringify(sortationWidget.value.data.optionsCorrectOrder) === JSON.stringify(studentOrder);

  return (
    <StudentWidgetWrapper>
      <TitleCaption>{formatMessage(widgetMessages.sortationWidgetCaption)}</TitleCaption>
      <Title>
        <div dangerouslySetInnerHTML={{ __html: sortationWidget.value.data.question }}></div>
      </Title>
      {!submitted && (
        <DragDropContext onDragEnd={dragEndHandle} onDragStart={onDragStartHandle} onDragUpdate={onDragUpdateHandle}>
          <DroppableWrapper>
            <Droppable droppableId="student-sortation-widget">
              {(provided, snapshot) => (
                <SortationOptions {...provided.droppableProps} innerRef={provided.innerRef}>
                  {studentOrder.map((option, index) => (
                    <Draggable key={option.guid} index={index} draggableId={option.guid}>
                      {(provided) => (
                        <SortationOption
                          innerRef={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          {...dropPlaceholder.placeholderProps}
                          dragged={option.guid === draggedOption}
                          optionText={option.text}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {dropPlaceholder.placeholderProps && snapshot.isDraggingOver && (
                    <DndPlaceholder placeholder={dropPlaceholder.placeholderProps} />
                  )}
                </SortationOptions>
              )}
            </Droppable>
          </DroppableWrapper>
        </DragDropContext>
      )}

      {submitted && (
        <SortationOptions>
          {(showCorrectAnswer ? sortationWidget.value.data.optionsCorrectOrder : studentOrder).map((option, index) => (
            <SortationOption
              key={option.guid}
              optionText={option.text}
              dragged={option.guid === draggedOption}
              variant={pickVariant(option, index)}
            />
          ))}
        </SortationOptions>
      )}

      {!submitted ? (
        <Footer>
          <StudentButton onClick={submit}>{formatMessage(quizMessages.submit)}</StudentButton>
        </Footer>
      ) : (
        <CSSTransition nodeRef={footerRef} timeout={500} classNames="fade" in appear>
          <Footer
            ref={footerRef}
            actions={
              !isOrderCorrect && (
                <>
                  {revealAnswers && (
                    <>
                      {!showCorrectAnswer ? (
                        <StudentButton onClick={() => setShowCorrectAnswerResult(true)}>
                          {formatMessage(quizMessages.showSolution)}
                        </StudentButton>
                      ) : (
                        <>
                          <StudentButton onClick={() => setShowCorrectAnswerResult(false)}>
                            {formatMessage(quizMessages.showTheirAnswer)}
                          </StudentButton>
                        </>
                      )}
                    </>
                  )}
                  {repeatUntilCorrect && (
                    <StudentButton onClick={tryAgain}>{formatMessage(quizMessages.tryAgain)}</StudentButton>
                  )}
                </>
              )
            }
          >
            <AnswerMessage variant={isOrderCorrect ? 'success' : 'fail'}>
              {isOrderCorrect
                ? formatMessage(widgetMessages.questionWidgetCorrectTryMessage)
                : repeatUntilCorrect
                ? formatMessage(widgetMessages.questionWidgetIncorrectTryMessage)
                : formatMessage(widgetMessages.questionWidgetIncorrectNoRetry)}
            </AnswerMessage>
          </Footer>
        </CSSTransition>
      )}
    </StudentWidgetWrapper>
  );
};
